import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Simplified Chinese translation of en.tsx. Every number comes from `limits`;
 * every claim about who can read what follows the threat model (PLAN.md 4.12). Keep it in step
 * with en.tsx.
 */
export function GuideZh({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Vrelis 如何运作</h1>
      <p className="lead">
        Vrelis 是一项发送加密消息（时间胶囊）的服务，胶囊只能从你选定的时刻起被阅读：由试运行中的所有人，或由
        某一个人。在那一刻之前，任何人都无法阅读胶囊：你写信的对象不行，运营 Vrelis 的人不行，你自己也不行。
      </p>
      <p className="warn">
        这是一个封闭的试运行。请下载你想保留的胶囊（见下文），并保管好你的恢复助记词。
      </p>

      <h2>为什么时间锁值得信赖</h2>
      <p>
        时间锁不是 Vrelis 的承诺。它由公开发表的密码学加上 Vrelis 无法控制的公共时间信号构成。下面说明是什么在支撑它，
        以及要让它失效，你需要相信什么。
      </p>

      <h3>drand：为时间签名的公共时钟</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> 是由{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>（熵联盟）运营的网络。这是一群相互独立的机构（其中包括 Cloudflare、Protocol Labs、Kudelski
          Security、EPFL 和伦敦大学学院），它们每 3 秒共同发布一个随机的签名值。这些值都有编号：第 1 轮、
          第 2 轮，依此类推，每一轮对应一个已知的时刻。任何人都可以从多个公共中继读取它们；但没有人能读取未来的值，因为它
          还不存在。
        </li>
        <li>
          每一轮都用门限密码学（<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS 签名</Ext>）签名：每个机构持有一个秘密密钥的一份，签名需要
          多数份额。任何单个机构，或其中的少数机构，都无法提前生成某一轮的签名。该网络自 2020 年
          起运行；我们使用的链{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext> 自 2023 年起运行。
          它的公钥固定写在我们的代码中，我们收到的每个信号都会据此校验。
        </li>
        <li>
          <b>时间锁加密</b>（<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>，由 Protocol Labs 和 Cloudflare 的研究人员
          于 2023 年发表）把这个时钟变成一把锁：消息被
          加密到某个轮次编号上，而该轮的签名一旦发布，就是打开它的唯一密钥。在 drand 发布之前，这把密钥在世界上
          任何地方都不存在。
        </li>
      </ul>

      <h3>Vrelis 如何使用它</h3>
      <ul>
        <li>
          你的浏览器把正文和文件的密钥加密到你所选时间对应的 drand 轮次上；发送前，编写页面会显示轮次编号。
          私密胶囊还会再加密一次，加密到收件人的密钥上，因此时间到后只有对方能打开。
        </li>
        <li>
          我们的服务器只接收并存储封存后的结果。它没有任何能打开胶囊的密钥：时间到之前没有，私密胶囊在时间到之后
          也没有。数据库泄露只会暴露密文和元数据，永远不会暴露消息内容。
        </li>
        <li>
          时间到时，你的浏览器（或离线打开工具）从四个公共 drand 中继之一获取该轮的签名，用固定的公钥
          校验，然后解密。Vrelis 不参与这一步。
        </li>
        <li>
          每个胶囊都由作者的密钥签名（<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>，与 Bitcoin
          和 <Ext href="https://nostr.com">Nostr</Ext> 使用的密钥类型相同），因此被改动的胶囊会被发现。密钥在你的浏览器中由你的 12 个词生成（
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>），从不以未加密的形式离开浏览器。
        </li>
      </ul>

      <h3>如果 Vrelis 消失了</h3>
      <ul>
        <li>
          格式是开放的：胶囊是一个 <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr 事件</Ext>（私密胶囊则是{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext>），其内容是一个带{" "}
          <Ext href="https://github.com/drand/tlock-js">tlock</Ext> 段的 <Ext href="https://age-encryption.org">age</Ext> 文件。任何
          实现了这些开放规范的软件都能打开你的胶囊；不一定需要我们的软件。
        </li>
        <li>
          <b>下载</b>会把胶囊连同离线打开工具一起保存到你自己的磁盘上：离线打开工具是一个 HTML 文件，可以在你的电脑上打开胶囊，不需要
          Vrelis 服务器，只需要所需的 drand 信号，以及（对于私密胶囊）你的密钥。完整的导出无需 vrelis.com 即可打开，前提是
          所需的信号可以获得，并且你保留了它所需的文件和密钥。
        </li>
        <li>
          如果 drand 本身在产生所需的未来信号之前就停止了，这些胶囊可能会永久保持锁定。提前下载
          并不能消除这个风险。开启日期已过的胶囊仍然需要它那个经过校验的确切信号：如果你的归档中没有这个信号，
          也无法从中继获取，仅凭日期无法离线打开它。请保留完整的导出及其所需的密钥。
        </li>
      </ul>

      <h3>要让它失效，你需要相信什么</h3>
      <ul>
        <li>
          相信 drand 的多数机构会串通起来提前签署某一轮。它们彼此独立，分布在多个国家，而且链是公开的，所以
          这样的签名会被所有人看到。这是整个系统的信任前提。
        </li>
        <li>
          相信网页在撒谎。网页是 Vrelis 发送到你浏览器的代码；控制这些代码的人可以在正文封存之前读取它，或在你解锁后读取你的
          密钥。正因如此，胶囊可以下载并离线打开，密钥指纹会显示出来供你在 Vrelis 之外
          核对，而且试运行之后计划推出一款不把密钥放在网页中的手机应用。
        </li>
        <li>
          相信今天的密码学被攻破了。drand 和你的密钥都使用的椭圆曲线密码学，可能会被未来的{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">量子计算机</Ext>攻破。
          对于要封存几十年的胶囊，这是一个真实的风险，试运行并没有解决它。
        </li>
      </ul>

      <h2>加入</h2>
      <ol>
        <li>
          如果管理员批准了你的地址，直接用它注册；否则打开你的邀请链接并使用其中的地址。用该 Google 账号登录（Google 只向 Vrelis 提供你的
          姓名和邮箱），或用密码登录（我们会发送邮件链接以确认地址）。之后你可以添加通行密钥用于登录。
        </li>
        <li>
          Vrelis 会提出在你的浏览器中生成你的<b>密钥</b>：这是你的主签名，为你发送的内容签名，并打开发给你的私密胶囊。你
          可以跳过，稍后在“账号”的“密钥”中生成；没有密钥时你只能阅读公开胶囊。在你的<b>恢复助记词</b>（12 个词，可以
          在任何设备上重建密钥）出现之前，系统会请你确认没有人能看到或录制你的屏幕。
        </li>
        <li>把这 12 个词抄在纸上。系统会请你输入其中三个进行核对，然后再次登录以确认是你本人：使用密码或通行密钥，如果是你的第一个密钥，也可以使用 Google。</li>
        <li>
          选择一个 PIN（至少 {l.pinDigits} 位数字）并创建<Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">通行密钥</Ext>（指纹、
          面容或设备 PIN）。它们共同保护此设备上的密钥，
          以及 Vrelis 为你迁移到新设备而保存的副本。如果你的浏览器无法以这种方式使用通行密钥，密钥在该浏览器中就没有保护，
          服务器上也没有副本；这时助记词是转到其他设备的唯一途径。
        </li>
      </ol>

      <h2>你的恢复助记词</h2>
      <ul>
        <li>把它写在纸上，放在安全的地方。不要拍照，也不要存放在电脑或密码管理器中。</li>
        <li>
          谁拥有这 12 个词，谁<b>就是</b>你：他们可以以你的身份签名，并打开你的私密胶囊。如果助记词泄露，请在“账号”的“密钥”中使用<b>重置密钥</b>：
          你输入助记词，再次登录，密钥就被永久撤销；任何人都不能再用它发送，也不能再向它发送。你说明它可能从何时
          起被盗，从那时起用它发送的胶囊会被标记为“已撤销的密钥”。然后你用新的助记词生成新密钥。
        </li>
        <li>
          你以前的密钥可以放进一个<b>密钥环</b>，用你当前的密钥加密，这样发给旧密钥的胶囊无需旧助记词也能打开。
          你也可以自己保管旧助记词，以后再把它添加到密钥环中。
        </li>
        <li>
          如果你丢失了一台设备：在另一台设备上登录，使用你的通行密钥和 PIN（如果通行密钥会同步）或你的助记词。如果你忘了 PIN：使用
          助记词。输错 {l.pinAttempts} 次 PIN 后，服务器上的副本会被锁定；助记词会写入一个新副本。
        </li>
        <li>
          如果你丢失了助记词<b>并且</b>丢失了所有存有密钥的设备，你的私密胶囊将永远无法被任何人打开。管理员只能给你一个
          新身份。撤销密钥也需要助记词，因此没有助记词时，由管理员替你撤销。
        </li>
      </ul>

      <h2>编写胶囊</h2>
      <ul>
        <li>
          <b>试运行中的所有人</b>（公开）或<b>一位成员</b>（私密）。公开胶囊在时间到后每位成员都能阅读；私密胶囊只有
          你写信的对象能阅读，如果你保留了副本（默认开启），你也能阅读。
        </li>
        <li>
          每个胶囊都需要一个<b>主题</b>。主题从不加密：任何看到胶囊的人都能立即读到，我们的服务器也能。
        </li>
        <li>
          <b>简介</b>是可选的：最多 {l.introChars} 个字符，在时间到之前、点击胶囊时就能阅读。公开胶囊的简介
          所有人都能读到，我们的服务器也能。私密胶囊的简介只有收件人能用其密钥读到；服务器看不到。无论哪种情况，消息正文及其
          文件都保持锁定。
        </li>
        <li>
          私密胶囊的发件人信息封存到收件人的密钥上：收件人用密钥打开胶囊后就能立即看到，远早于开启时间。
          没有该密钥而拿到胶囊的人只能看到主题和时间。我们的服务器知道是哪个账号发送的。它只告诉收件人，并加密到对方的密钥上，以便应用核对签名胶囊的密钥是否属于该账号；如果不属于，就显示“未经验证的发件人”，而不是名字。
        </li>
        <li>
          <b>隐藏发件人</b>（私密胶囊）：在收件人点击胶囊之前，对方的列表中显示“隐藏的发件人”而不是你的名字，这样
          越过对方肩膀偷看的人就看不到是谁写信给对方。收件人在时间到之前仍然可以看到是你。
        </li>
        <li>
          选择开启时间：5 分钟、1 小时、1 天、1 周、1 年后，或 2125 年之前的某个日期。发送前会显示实际的开启时间；
          精确到几秒以内。
        </li>
        <li>
          正文：公开胶囊最多 {l.publicText}，私密胶囊最多 {l.privateText}。文件：最多 {l.files} 个，总大小在免费版中为 {l.fileBytes}，在元老版或入门版中为 {l.planFileBytes}。照片
          会被压缩，并移除包括位置在内的元数据。
        </li>
        <li>
          <b>封存并发送</b>会请你输入一次 PIN 并使用一次通行密钥，用于为胶囊签名。此后任何人都无法修改它，在时间到之前
          也没有人能阅读它。
        </li>
        <li>
          在给某人发送私密胶囊之前，请核对对方的密钥：编写页面会显示它的指纹（16 个字符）。如果对方的密钥自此浏览器上次
          看到后已更换，编写页面会提醒你，并等待你在 Vrelis 之外与对方确认新密钥确实属于对方。
        </li>
      </ul>

      <h2>阅读</h2>
      <ul>
        <li>
          <b>动态</b>：公开胶囊。<b>收件箱</b>：发给你的私密胶囊。<b>已发送</b>：你的胶囊。<b>关注的消息</b>：你在等待的公开胶囊。关注
          一位作者，即可只看对方的胶囊。
        </li>
        <li>
          <b>隐藏</b>一位成员：对方的公开胶囊会在你所有设备的动态中消失；对方不受任何影响。<b>屏蔽</b>一位成员：对方不能
          再把私密胶囊放进你的收件箱（编写页面会在对方发送前提示）；你仍然可以关注对方并阅读对方的公开胶囊。
          对方以前发的胶囊会移到“收件箱”的“已屏蔽”中，仍然可以打开。这两项操作都在某人的菜单和联系人页面中，那里还能看到谁关注了你。
          Vrelis 只为它接收和投递的内容负责：没有人能阻止别人在 Vrelis 之外用你的公钥加密内容。
        </li>
        <li>
          私密胶囊在你打开之前不显示任何内容：<b>打开</b>会请你输入 PIN，然后显示发件人和开启时间，时间到后
          显示正文。
        </li>
        <li>
          你的密钥保持锁定；每个需要它的操作都会请你输入 PIN。要连续阅读多个胶囊，请使用顶部栏中的<b>解锁 … 分钟</b>。
          <b>锁定</b>会立即隐藏所有私密内容；离开页面也会如此。
        </li>
        <li>
          在<a href="/account">账号</a>的“隐私”中：“在我的列表中，点击胶囊后才显示发件人”会把每个私密胶囊都当作隐藏发件人处理，
          在所有设备上生效。
        </li>
        <li>
          列表每次显示 50 个胶囊；<b>显示更多</b>会加载后续的胶囊。由 Vrelis 筛选和排序（按开启时间，或按它收到
          胶囊的时间），因此筛选或分组发送给 Vrelis 的是你所选成员的 ID，永远不是分组或分组名称。按发件人筛选收件箱需要
          你的密钥，会排除隐藏发件人的胶囊，并且在发件人需点击后才显示时不可用。
        </li>
        <li>
          <b>搜索</b>的范围包括主题、公开胶囊的简介、公开胶囊的作者，以及你发给了谁。它从不搜索已打开的正文
          （只有你的设备上有），也不搜索谁给你发了私密胶囊。
        </li>
      </ul>

      <h2>回信信封、抄送和往来消息</h2>
      <ul>
        <li>
          私密胶囊可以附带一个<b>回信信封</b>：你请每位收件人给你回一条私密回复，封存到你的密钥上。
          即使对方没有关注你，也可以回复。你可以选择让每条回复在你设定的时间开启（过了该时间不再
          接受回复），或在收件人选择的时间开启。你预先为胶囊和回复付费：每位收件人 2 张邮票，而不是 1 张。收件人的第一条回复不花对方任何费用；第二条回复由对方支付一张邮票，你会看到两条。
        </li>
        <li>
          <b>抄送</b>：发给一位成员的私密胶囊可以同时发给更多成员，列出的每个人都能看到还有谁收到了。名单
          连同发件人的签名一起封存在胶囊内部；我们的服务器永远看不到它。不使用抄送的群发保持不公开：没有人能看到
          还有谁收到了。
        </li>
        <li>
          带回信信封的胶囊及其回复都在<b>往来消息</b>页面中，而不在收件箱或已发送中：在每个
          请求下面，你可以按顺序看到每位收件人的回复（在各自的时间之前保持锁定），以及每条回复由谁付费。回复只发给发件人。
        </li>
      </ul>

      <h2>不依赖 Vrelis 保存胶囊</h2>
      <ul>
        <li>
          胶囊上的<b>下载</b>会把它保存为一个文件；带附件时则是一个 .zip，包含文件和离线打开工具。选择多个胶囊并使用{" "}
          <b>下载所选</b>即可得到一个归档（“全选显示的项目”会选中当前显示的胶囊）。
        </li>
        <li>
          离线打开工具是一个 HTML 文件（在<a href="/account">账号</a>中，或在 .zip 内）。它在你自己的电脑上打开你的胶囊，私密胶囊需配合你的助记词。它
          从不连接 vrelis.com；只有当文件中还没有开启信号时，它才会向公共 drand 时间服务请求该信号。
        </li>
        <li>
          “包含已打开的正文和文件”会把已打开的正文和文件以不加密的形式放进下载内容：任何拿到该文件的人都能阅读。
        </li>
      </ul>

      <h2>此设备</h2>
      <ul>
        <li>
          <b>锁定</b>：从内存中移除密钥。<b>退出登录</b>：结束本次会话；密钥保留在此设备上。<b>退出所有设备</b>：
          结束你在所有设备上的会话。<b>忘记此设备</b>：删除你的密钥以及 Vrelis 在此浏览器中存储的所有内容。
        </li>
        <li>一次登录持续 {l.sessionDays} 天。</li>
      </ul>

      <h2>Vrelis 能看到什么，不能看到什么</h2>
      <ul>
        <li>
          <b>Vrelis 无法阅读</b>时间到之前的胶囊，永远无法阅读私密胶囊（它没有相应的密钥），也无法读取你的密钥、PIN 或助记词。
        </li>
        <li>
          <b>Vrelis 能看到</b>：你的姓名和邮箱（所有者和管理员能看到邮箱）、谁发送了胶囊以及发给了谁、发送时间、公开胶囊的
          开启时间、大小，以及胶囊是否带有文件。公开胶囊在时间到后每位成员都能阅读，因此 Vrelis 也能阅读。
        </li>
        <li>
          密钥环和你的密钥副本只以加密形式存储；Vrelis 无法打开它们。
        </li>
        <li>
          签名证明是哪个密钥编写了胶囊。标记为“已撤销的密钥”的胶囊，是由其所有者后来报告被盗的密钥签名的。密钥属于某个人，这一点依赖于成员列表以及发件人写入
          胶囊的名字。请与你私下写信的人核对指纹。
        </li>
      </ul>

      <h2>方案与邮票</h2>
      <ul>
        <li>
          一张<b>邮票</b>支付向一位收件人的一次投递。你自己的副本免费。免费版每月提供 {l.freeStamps} 张邮票、{l.freeStorage} 存储空间，每个胶囊的文件 {l.fileBytes}；元老版
          （每月 {l.legacyStamps} 张邮票，{l.planStorage}，{l.planFileBytes}）授予了首批成员；入门版将每月提供 {l.planStamps} 张邮票。当月的邮票不会结转，会在下个月的
          第一天（UTC）重新发放。<b>账单</b>显示你拥有什么、还剩多少以及计划内容。
        </li>
        <li>
          目前不收取任何费用，也无法购买任何东西：方案只是预览，由管理员手动开通申请。无论你的方案或邮票发生什么变化，你已发送的内容对你和收件人都仍然可以阅读。
        </li>
        <li>
          给自己的副本是可选的，默认开启。如果关闭，只有收件人能阅读该胶囊：你自己将无法阅读，即使开启后也不行。
        </li>
      </ul>

      <h2>试运行的限制</h2>
      <ul>
        <li>
          每天最多 {l.daily} 次投递（你自己的副本免费；发给分组的胶囊按每位成员计一次）。上传：每 24 小时 {l.uploadDaily} 和 {l.unusedFiles} 个未使用的文件；
          24 小时内未用于胶囊的文件会被删除。
        </li>
        <li>可以用 Google、密码或通行密钥登录。没有付款，没有公共网络：胶囊只在试运行内部流转。</li>
        <li>
          邀请一个已有账号的地址时，会改为向该成员发送联系请求；如果对方接受，你们会互相关注。无论哪种情况，你的
          屏幕看起来都一样，因此没有人能利用邀请来查出谁是成员。
        </li>
      </ul>

      <h2>面向开发者</h2>
      <p>
        服务可以通过 <a href="/developers">Vrelis API</a> 阅读和发送胶囊。服务通过管理员发出的服务邀请获得自己的账号；
        在其他人看来，它是一位普通成员。它生成自己的密钥，并自行为每个胶囊签名；它的 API 令牌可以阅读和发送，但
        不能管理密钥或登录方式。你可以像对待其他人一样屏蔽或隐藏服务。
      </p>

      <h2>我们对试运行成员的请求</h2>
      <ol>
        <li>发送一个 1 小时后开启的公开胶囊，以及一个 1 天后开启、发给另一位成员的私密胶囊。</li>
        <li>在公开胶囊开启后阅读它们，并试着在开启前阅读一个：你应该无法做到。</li>
        <li>请其中一人：在第二台设备上设置你的密钥，并在那里阅读你的收件箱。</li>
        <li>请其中一人：隐藏另一位成员，并确认对方的胶囊只从你的动态中消失。</li>
      </ol>

      <h2>问题</h2>
      <p>
        请使用每个页面底部的<b>报告问题</b>，或写信至 {link}。说明你做了什么、期望发生什么以及实际发生了什么；附上截图会有帮助。
        服务、其限制和责任在<a href="/terms">使用条款</a>中有说明。
      </p>
    </>
  );
}
